import React, { useState } from 'react';
import { Modal, Typography, Row, Col, Button, Space } from 'antd';
import { 
  SunOutlined, 
  ThunderboltOutlined,
  CloseOutlined,
  ArrowRightOutlined,
  CheckCircleOutlined,
  PoweroffOutlined
} from '@ant-design/icons';
import './SolarPrivadoModal.css';

const { Title, Text, Paragraph } = Typography;

interface SolarPrivadoModalProps {
  visible: boolean;
  onClose: () => void;
  onSelectProposal: (proposalType: string) => void;
}

const SolarPrivadoModal: React.FC<SolarPrivadoModalProps> = ({
  visible,
  onClose,
  onSelectProposal
}) => {

  const proposalTypes = [
    {
      id: 'sem_bateria',
      title: 'Proposta sem Bateria',
      subtitle: 'Sistema Solar Básico',
      description: 'Instalação de painéis solares para produção de energia elétrica sem sistema de armazenamento.',
      icon: <SunOutlined />,
      color: '#2ecc71',
      gradient: 'linear-gradient(135deg, rgba(46, 204, 113, 0.1) 0%, rgba(39, 174, 96, 0.1) 100%)',
      features: [
        'Painéis solares de alta eficiência',
        'Inversor solar otimizado',
        'Sistema de monitorização',
        'Instalação e configuração'
      ]
    },
    {
      id: 'com_bateria',
      title: 'Proposta com Bateria',
      subtitle: 'Sistema Solar + Armazenamento',
      description: 'Sistema completo com painéis solares e bateria para armazenamento de energia.',
      icon: <PoweroffOutlined />,
      color: '#3498db',
      gradient: 'linear-gradient(135deg, rgba(52, 152, 219, 0.1) 0%, rgba(41, 128, 185, 0.1) 100%)',
      features: [
        'Painéis solares de alta eficiência',
        'Sistema de baterias Tesla Powerwall',
        'Inversor híbrido inteligente',
        'Backup automático em caso de falha'
      ]
    },
    {
      id: 'bateria_opcional',
      title: 'Proposta Bateria Opcional',
      subtitle: 'Sistema Flexível',
      description: 'Sistema solar com possibilidade de adicionar bateria posteriormente.',
      icon: <ThunderboltOutlined />,
      color: '#f39c12',
      gradient: 'linear-gradient(135deg, rgba(243, 156, 18, 0.1) 0%, rgba(230, 126, 34, 0.1) 100%)',
      features: [
        'Painéis solares de alta eficiência',
        'Inversor compatível com baterias',
        'Infraestrutura preparada',
        'Upgrade futuro garantido'
      ]
    }
  ];

  const handleProposalSelect = (proposalType: string) => {
    onSelectProposal(proposalType);
    onClose();
  };

  return (
    <Modal
      open={visible}
      onCancel={onClose}
      footer={null}
      width="90%"
      style={{ maxWidth: '1400px' }}
      className="solar-privado-modal"
      closeIcon={<CloseOutlined style={{ color: 'white', fontSize: '1.5rem' }} />}
    >
      <div className="modal-background">
        <div className="modal-content">
          <div className="modal-header">
            <div className="header-icon">
              <SunOutlined />
            </div>
            <Title level={2} className="modal-title">
              Solar Privado - Simulador
            </Title>
            <Paragraph className="modal-subtitle">
              Escolha o tipo de proposta que melhor se adequa às suas necessidades energéticas
            </Paragraph>
          </div>

          <div className="proposals-grid">
            <Row gutter={[32, 32]} justify="center">
              {proposalTypes.map((proposal) => (
                <Col xs={24} lg={8} key={proposal.id}>
                  <div
                    className={`proposal-card ${proposal.id}`}
                    style={{
                      background: proposal.id === 'sem_bateria' ? 'rgba(100, 200, 100, 0.6)' :
                                 proposal.id === 'com_bateria' ? 'rgba(100, 150, 255, 0.6)' :
                                 proposal.id === 'bateria_opcional' ? 'rgba(255, 180, 100, 0.6)' :
                                 'transparent',
                      border: proposal.id === 'sem_bateria' ? '2px solid rgba(120, 220, 120, 0.7)' :
                              proposal.id === 'com_bateria' ? '2px solid rgba(120, 170, 255, 0.7)' :
                              proposal.id === 'bateria_opcional' ? '2px solid rgba(255, 200, 120, 0.7)' :
                              'none',
                      boxShadow: proposal.id === 'sem_bateria' ? '0 8px 32px rgba(100, 200, 100, 0.5)' :
                                 proposal.id === 'com_bateria' ? '0 8px 32px rgba(100, 150, 255, 0.5)' :
                                 proposal.id === 'bateria_opcional' ? '0 8px 32px rgba(255, 180, 100, 0.5)' :
                                 'none',
                      borderRadius: '20px',
                      padding: '0',
                      height: '100%',
                      position: 'relative',
                      overflow: 'hidden',
                      backdropFilter: 'blur(20px)',
                      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'
                    }}
                  >
                    <div className="card-header">
                      <div className="card-icon-wrapper">
                        <div className="card-icon" style={{ color: proposal.color }}>
                          {proposal.icon}
                        </div>
                      </div>
                      <div className="card-info">
                        <Title level={3} className="card-title">
                          {proposal.title}
                        </Title>
                        <Text className="card-subtitle">
                          {proposal.subtitle}
                        </Text>
                        <Paragraph className="card-description">
                          {proposal.description}
                        </Paragraph>
                      </div>
                    </div>

                    <div className="card-features">
                      <Space direction="vertical" size="small" className="features-list">
                        {proposal.features.map((feature, index) => (
                          <div key={index} className="feature-item">
                            <CheckCircleOutlined className="feature-icon" style={{ color: proposal.color }} />
                            <Text className="feature-text">{feature}</Text>
                          </div>
                        ))}
                      </Space>
                    </div>

                    <div className="card-footer">
                      <Button
                        type="primary"
                        size="large"
                        className="select-button"
                        onClick={() => handleProposalSelect(proposal.id)}
                        icon={<ArrowRightOutlined />}
                        style={{ 
                          background: proposal.color,
                          borderColor: proposal.color,
                          color: 'white',
                          boxShadow: `0 6px 20px ${proposal.color}40`
                        }}
                      >
                        Selecionar Proposta
                      </Button>
                    </div>
                  </div>
                </Col>
              ))}
            </Row>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default SolarPrivadoModal; 